// src/pages/Menu/Articulacion/Documents/ProjectsDetails/ProjectDetails.jsx
import React, { useEffect, useRef } from "react";
import { ArrowLeft, Upload, Edit, Trash2, Calendar, MapPin, Eye, FileText, Tag } from "lucide-react";
import { GradientCard, AccentCard, Segmented, Skeleton, csvToArr, fmtNiceRange, fmtOnlyDate, statusCfg, getStatusLabel } from "./ProjectDetailsComponents";

/* Helper para quitar extensión PDF */
const stripPdfExt = (s) => String(s || "").replace(/\.pdf$/i, "");

export default function ProjectDetails({ 
  selected, 
  detailTab, 
  setDetailTab,
  minutes,
  minuteFilters,
  setMinuteFilters,
  ui,
  onBack,
  onUploadMinute,
  onEdit,
  onDelete,
  onEditMinute,
  onDeleteMinute,
  catLabels,
  input,
  btnGhost,
  hideBanner = false
}) {
  const detailsRef = useRef(null);

  if (!selected) {
    return (
      <GradientCard>
        <div className="h-full min-h-[300px] rounded-2xl bg-white grid place-items-center text-gray-500 p-8">
          <div className="text-center space-y-2">
            <div className="text-4xl opacity-30">📋</div>
            <p className="text-sm font-medium">Selecciona un proyecto</p>
            <p className="text-xs text-gray-400">para ver su información y minutas</p>
          </div>
        </div>
      </GradientCard>
    );
  }

  return (
    <div ref={detailsRef} className="w-full lg:h-full lg:flex lg:flex-col">
        {/* ===== CONTENIDO por pestaña ===== */}
        {detailTab === "summary" && (
          <>
            {/* Banner blanco de separación */}
            <div className="h-4 bg-white shrink-0"></div>
            <AccentCard
              titleSlot={
                <div className="flex items-center gap-2 text-[12px] font-semibold tracking-wide text-white">
                    <FileText size={14}/> Resumen del proyecto
                </div>
              }
              className="lg:flex-1 lg:min-h-0 lg:h-full"
              compactHeader={true}
            >
            <div className="p-5 md:p-6 space-y-5 h-full flex flex-col overflow-hidden">
              <div className="flex flex-col gap-4 flex-1 min-h-0 overflow-hidden">
                {/* Descripción */}
                <section className="rounded-xl border-2 border-slate-200 bg-gradient-to-br from-slate-50 to-white overflow-hidden flex flex-col" style={{ height: '300px' }}>
                  <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50/30 border-b border-slate-200 shrink-0">
                    <div className="flex items-center gap-2 text-[13px] font-semibold text-gray-900">
                      <FileText size={16} className="text-[#0488AF]"/> Descripción
                    </div>
                  </div>
                  <div className="p-4 overflow-y-auto flex-1 min-h-0">
                    {selected.description ? (
                      <p className="leading-relaxed whitespace-pre-line text-gray-900 w-full">
                        {selected.description}
                      </p>
                    ) : (
                      <div className="text-sm text-gray-500 italic w-full">Sin descripción</div>
                    )}
                  </div>
                </section>

                {/* Etiquetas */}
                <aside className="rounded-xl border-2 border-slate-200 bg-white overflow-hidden flex flex-col">
                  <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50/30 border-b border-slate-200 shrink-0">
                    <div className="flex items-center gap-2 text-[13px] font-semibold text-gray-900">
                      <Tag size={16} className="text-[#0488AF]"/> Etiquetas
                    </div>
                  </div>
                  <div className="p-4 overflow-auto min-h-0 h-[200px]">
                    {(() => {
                      const tags = csvToArr(selected.tags || selected.collaborators || "");
                      return tags.length ? (
                        <div className="flex flex-wrap gap-1.5 w-full">
                          {tags.map((t,i)=>(
                            <span key={`${t}-${i}`} className="px-2 py-[2px] rounded-full border text-[10px] bg-white">
                              {t}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-500 w-full">Sin etiquetas</span>
                      );
                    })()}
                  </div>
                </aside>
              </div>
            </div>
          </AccentCard>
          </>
        )}

        {detailTab === "minutes" && (
          <AccentCard
            titleSlot={
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[13px] font-semibold tracking-wide text-white">
                  <FileText size={16}/> Minutas
                </div>
                {minutes.length > 0 && (
                  <span className="text-xs bg-white/20 px-2.5 py-1 rounded-full">
                    {minutes.length} {minutes.length === 1 ? "minuta" : "minutas"}
                  </span>
                )}
              </div>
            }
            className="lg:flex-1 lg:min-h-0 lg:h-full"
            compactHeader={true}
          >
            <div className="p-5 md:p-6 h-full flex flex-col overflow-hidden">
              {/* Filtros - No scroll */}
              <div className="shrink-0 mb-4">
                <div className="rounded-xl bg-gradient-to-br from-slate-50 to-white border border-slate-200 p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <label className="text-xs font-semibold text-[#09274A] uppercase tracking-wide">
                      Subidas desde
                      <div className="relative mt-2">
                        <input
                          type="date"
                          value={minuteFilters.dateFrom}
                          onChange={(e) => {
                            setMinuteFilters((f) => ({ ...f, dateFrom: e.target.value }));
                          }}
                          className={`h-10 w-full rounded-lg px-3 pr-9 text-sm text-[#09274A] ${input} placeholder:text-gray-400`}
                        />
                        <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"/>
                      </div>
                    </label>
                    <label className="text-xs font-semibold text-[#09274A] uppercase tracking-wide">
                      Subidas hasta
                      <div className="relative mt-2">
                        <input
                          type="date"
                          value={minuteFilters.dateTo}
                          onChange={(e) => {
                            setMinuteFilters((f) => ({ ...f, dateTo: e.target.value }));
                          }}
                          className={`h-10 w-full rounded-lg px-3 pr-9 text-sm text-[#09274A] ${input} placeholder:text-gray-400`}
                        />
                        <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"/>
                      </div>
                    </label>
                  </div>
                  {(minuteFilters.dateFrom || minuteFilters.dateTo) && (
                    <div className="mt-4 flex justify-end">
                      <button
                        type="button"
                        onClick={() => {
                          setMinuteFilters({ dateFrom: "", dateTo: "" });
                        }}
                        className="inline-flex items-center justify-center h-9 px-4 rounded-full border border-[#DC3465] text-[#DC3465] text-xs font-semibold tracking-wide uppercase hover:bg-[#C42251] hover:text-white transition-colors"
                      >
                        Limpiar
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Lista minutas - Con scroll */}
              <div className="flex-1 min-h-0 overflow-y-auto">
                {ui.minutesLoading ? (
                  <div className="space-y-3">
                    {[...Array(3)].map((_,i)=><div key={i} className="h-16 rounded-xl bg-slate-100 animate-pulse border border-slate-200"/>)}
                  </div>
                ) : minutes.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FileText size={32} className="mx-auto mb-2 opacity-50"/>
                    <p className="text-sm">No hay minutas</p>
                  </div>
                ) : (
                  <div className="space-y-3 pr-2">
                    {minutes.map((m) => {
                      const url = m.viewUrl || m.fileSecureUrl || m.fileUrl;
                      const deleting = ui.deletingMinuteId === m.id;
                      const title = m.title || stripPdfExt(m.fileName || m.originalName || m.name || "Minuta");
                      return (
                        <div key={m.id} className="group p-4 rounded-xl border-2 border-slate-200 bg-white hover:border-blue-200 hover:shadow-md transition-all">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              <div className="h-10 w-10 shrink-0 grid place-items-center rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200">
                                <FileText size={20} className="text-[#0488AF]"/>
                              </div>
                              <div className="min-w-0 flex-1">
                                {url ? (
                                  <a href={url} target="_blank" rel="noreferrer" className="font-semibold hover:underline break-words text-gray-900 block mb-1">
                                    {title}
                                  </a>
                                ) : (
                                  <div className="font-semibold break-words text-gray-900 mb-1">{title}</div>
                                )}
                                <div className="flex items-center gap-3 text-xs text-gray-600">
                                  {m.date && (
                                    <span className="inline-flex items-center gap-1">
                                      <Calendar size={12}/>{fmtOnlyDate(m.date)}
                                    </span>
                                  )}
                                  <span className="inline-flex items-center gap-1">
                                    <FileText size={12}/>Subida: {fmtOnlyDate(m.createdAt || m.uploadedAt || m.date)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <button onClick={()=>{ if(url) window.open(url,"_blank","noopener,noreferrer"); }}
                                      className="h-9 w-9 grid place-items-center rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-300 disabled:opacity-40 disabled:cursor-not-allowed transition" 
                                      title={url?"Abrir PDF":"Sin archivo"} disabled={!url}>
                                <Eye size={16} className="text-gray-700"/>
                              </button>
                              <button className="h-9 w-9 grid place-items-center rounded-lg bg-gradient-to-r from-[#09274A] to-[#0488AF] hover:from-[#071f3d] hover:to-[#03779C] transition-colors" 
                                      onClick={()=>onEditMinute(m)} title="Editar">
                                <Edit size={16} className="text-white"/>
                              </button>
                              <button className="h-9 w-9 grid place-items-center rounded-lg bg-[#C42251] hover:bg-[#A01D45] transition-colors" 
                                      onClick={()=>onDeleteMinute(m)} disabled={deleting} title="Eliminar">
                                {deleting ? "…" : <Trash2 size={16} className="text-white"/>}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </AccentCard>
        )}
    </div>
  );
}

// Componente PdfIcon
function PdfIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className="block" fill="none" aria-hidden>
      <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-6Z"
            stroke="#0488AF" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <path d="M14 2v6h6" stroke="#0488AF" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
      <text x="12" y="14.5" fontSize="7" fontWeight="800" fill="#0488AF" textAnchor="middle" dominantBaseline="middle">PDF</text>
    </svg>
  );
}

